import { DocPage, Locale } from './types';

/**
 * The documentation's search: an index of the pages' sections, built in the browser from the pages the user may
 * read, in their language. No server, no library: a few hundred sections, matched word by word.
 */

/** Lower case, without accents: "Créer" and "creer" are the same word. */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** A heading's anchor, the same for the rendered page (Markdown's slugify) and the search results. */
export function slugify(text: string): string {
  return normalize(text)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Markdown down to the words a reader sees: no images, HTML tags (`<kbd>`), link targets or table rules. */
export function plainText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s*\|?\s*:?-{3,}.*$/gm, ' ')
    .replace(/[`*_>#|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type Section = {
  pageId: string;
  pageTitle: string;
  /** Null for the text before the page's first `##` heading. */
  heading: string | null;
  anchor: string | null;
  text: string;
  /** The normalized title, heading and text, matched against the query. */
  searchable: { title: string; heading: string; text: string };
};

/** A page cut at its `##` headings. */
export function sectionsOf(page: DocPage, locale: Locale): Section[] {
  const { title, summary, body } = page.text[locale];
  const parts: { heading: string | null; lines: string[] }[] = [
    { heading: null, lines: [summary] },
  ];
  for (const line of body.split('\n')) {
    const heading = line.match(/^##\s+(.+?)\s*$/);
    if (heading) {
      parts.push({ heading: heading[1], lines: [] });
    } else {
      parts[parts.length - 1].lines.push(line);
    }
  }
  return parts.map(({ heading, lines }) => {
    const text = plainText(lines.join('\n'));
    return {
      pageId: page.id,
      pageTitle: title,
      heading,
      anchor: heading ? slugify(heading) : null,
      text,
      searchable: {
        title: normalize(title),
        heading: normalize(heading ?? ''),
        text: normalize(text),
      },
    };
  });
}

export function buildIndex(pages: DocPage[], locale: Locale): Section[] {
  return pages.flatMap(page => sectionsOf(page, locale));
}

export type SearchResult = {
  section: Section;
  score: number;
  /** The text around the first match, with the matched words as `[start, end)` ranges into it. */
  snippet: string;
  highlights: [number, number][];
};

const SNIPPET = 160;

/** The query's words: two letters or more, normalized. */
export function terms(query: string): string[] {
  return normalize(query)
    .split(/[^a-z0-9]+/)
    .filter(term => term.length >= 2);
}

/** How many times `term` starts a word of `text`. */
function wordStarts(text: string, term: string): number {
  let count = 0;
  let at = text.indexOf(term);
  while (at !== -1) {
    if (at === 0 || !/[a-z0-9]/.test(text[at - 1])) {
      count++;
    }
    at = text.indexOf(term, at + term.length);
  }
  return count;
}

function snippetOf(
  section: Section,
  words: string[]
): Pick<SearchResult, 'snippet' | 'highlights'> {
  const { text } = section;
  const searchable = section.searchable.text;
  const first = Math.min(
    ...words.map(word => searchable.indexOf(word)).filter(at => at !== -1)
  );
  const start = Number.isFinite(first) ? Math.max(0, first - SNIPPET / 3) : 0;
  // start and end on a word boundary
  const from = start === 0 ? 0 : text.indexOf(' ', start) + 1;
  const toRaw = Math.min(text.length, from + SNIPPET);
  const to =
    toRaw === text.length
      ? toRaw
      : Math.max(from, text.lastIndexOf(' ', toRaw));
  const prefix = from > 0 ? '… ' : '';
  const suffix = to < text.length ? ' …' : '';
  const snippet = prefix + text.slice(from, to) + suffix;
  // normalize() keeps the length of text without combining accents, so offsets carry over
  const normalized = normalize(snippet);
  const highlights: [number, number][] = [];
  for (const word of words) {
    let at = normalized.indexOf(word);
    while (at !== -1) {
      if (at === 0 || !/[a-z0-9]/.test(normalized[at - 1])) {
        highlights.push([at, at + word.length]);
      }
      at = normalized.indexOf(word, at + word.length);
    }
  }
  highlights.sort((a, b) => a[0] - b[0]);
  return { snippet, highlights: mergeRanges(highlights) };
}

function mergeRanges(ranges: [number, number][]): [number, number][] {
  const merged: [number, number][] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
    } else {
      merged.push([...range]);
    }
  }
  return merged;
}

/**
 * The sections holding every word of the query, as the start of a word ("valid" finds "validate"), best first:
 * a word in the page's title counts most, then in the section's heading, then each time it is in the text.
 */
export function search(
  index: Section[],
  query: string,
  limit = 20
): SearchResult[] {
  const words = terms(query);
  if (words.length === 0) {
    return [];
  }
  const results: SearchResult[] = [];
  for (const section of index) {
    const { title, heading, text } = section.searchable;
    let score = 0;
    let matchesAll = true;
    for (const word of words) {
      const inTitle = wordStarts(title, word);
      const inHeading = wordStarts(heading, word);
      const inText = wordStarts(text, word);
      if (inTitle + inHeading + inText === 0) {
        matchesAll = false;
        break;
      }
      score +=
        8 * Math.min(inTitle, 1) +
        4 * Math.min(inHeading, 1) +
        Math.min(inText, 5);
    }
    if (matchesAll) {
      results.push({ section, score, ...snippetOf(section, words) });
    }
  }
  return results
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.section.pageTitle.localeCompare(b.section.pageTitle)
    )
    .slice(0, limit);
}
